import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { dataService } from "../../lib/dataService.js";
import { C, font, mono, sel } from "../../lib/styles.js";
import { feedbackApi } from "../../lib/feedback/feedbackCallables.js";
import { pageLink } from "../../lib/feedback/deepLink.js";
import {
  DEFAULT_FILTERS, SOURCE_LABELS, STATUSES, STATUS_LABELS, TYPE_LABELS, counts, describeBrowser, describeIds,
  filterChoices, filterRows, plannerRow, plannerStoredStatus, sortRows, suiteRow,
} from "../../lib/feedback/model.js";

/**
 * The feedback inbox (Website > Feedback), one list for:
 *   - Suite: the Feedback tab on every page (bugs, suggestions, questions),
 *     read and changed through the admin-only callables;
 *   - Website planner: staff notes on planner sessions (Prospects > a
 *     session > Give Feedback), plannerSessions/{id}/feedback/{id}.
 * Open and doing first, bugs first among them, then newest. Each item opens
 * to its details: the words, the page (with a link that opens it), what was
 * open, the device, the build, recent errors, the picture, status and note.
 */

function formatDate(ms) {
  if (!ms) return "—";
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function formatTime(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

async function loadPlannerRows() {
  const sessions = await dataService.plannerSessions.loadAll();
  const out = [];
  await Promise.all(sessions.map(async (s) => {
    const fb = await dataService.feedback.loadForSession(s._docId);
    fb.forEach((f) => out.push(plannerRow({
      ...f,
      sessionWhat: s.subjectData?.description || (s.captureTypes || []).join(", ") || "Planner session",
    })));
  }));
  return out;
}

export default function FeedbackListView({ user, api = feedbackApi, loadPlanner = loadPlannerRows }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suiteNote, setSuiteNote] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [openKey, setOpenKey] = useState(null);
  const [reload, setReload] = useState(0);
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [planner, suite] = await Promise.all([
        loadPlanner().catch(() => []),
        isAdmin
          ? api.list().then((r) => ({ items: r?.items || [], truncated: !!r?.truncated })).catch((e) => ({ error: e }))
          : Promise.resolve({ items: [], skipped: true }),
      ]);
      if (cancelled) return;
      let note = "";
      if (suite.skipped) note = "Feedback from the Suite's Feedback tab is shown to admins only.";
      else if (suite.error) note = "Couldn't load the Suite's feedback. Reload the page to try again.";
      else if (suite.truncated) note = "The inbox is very full: some open Suite reports are left out of this list (node tools/feedback.js lists them).";
      setSuiteNote(note);
      setRows([...(suite.items || []).map(suiteRow), ...planner]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [api, loadPlanner, isAdmin, reload]);

  const replaceRow = useCallback((row) => setRows((rs) => rs.map((r) => (r.key === row.key ? row : r))), []);

  const choices = useMemo(() => filterChoices(rows), [rows]);
  const shown = useMemo(() => sortRows(filterRows(rows, filters)), [rows, filters]);
  const totals = useMemo(() => counts(rows), [rows]);
  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  if (loading) {
    return <div style={styles.empty}>Loading feedback…</div>;
  }

  return (
    <div style={{ fontFamily: font }}>
      <div style={styles.summary}>
        <span>{totals.active} open</span>
        {totals.bugs > 0 && <span style={{ color: C.danger, fontWeight: 400 }}> {"·"} {totals.bugs} {totals.bugs === 1 ? "bug" : "bugs"}</span>}
        <span> {"·"} {totals.total} in all</span>
        <button type="button" style={styles.linkBtn} onClick={() => { setLoading(true); setReload((n) => n + 1); }}>Refresh</button>
      </div>
      {suiteNote && <div style={styles.note}>{suiteNote}</div>}

      <div className="feedback-filters" style={styles.filters}>
        <button
          type="button"
          aria-pressed={filters.type === "bug"}
          style={{ ...styles.chip, ...(filters.type === "bug" ? styles.chipOn : {}) }}
          onClick={() => setFilter("type", filters.type === "bug" ? "all" : "bug")}
        >
          Bugs only
        </button>
        <Filter label="Status" value={filters.status} onChange={(v) => setFilter("status", v)}
          options={[{ value: "active", label: "Open & doing" }, ...STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] })), { value: "all", label: "All" }]} />
        <Filter label="Kind" value={filters.type} onChange={(v) => setFilter("type", v)}
          options={[{ value: "all", label: "All" }, ...["bug", "suggestion", "question", "planner"].map((t) => ({ value: t, label: TYPE_LABELS[t] }))]} />
        <Filter label="From" value={filters.source} onChange={(v) => setFilter("source", v)}
          options={[{ value: "all", label: "Everywhere" }, { value: "suite", label: SOURCE_LABELS.suite }, { value: "planner", label: SOURCE_LABELS.planner }]} />
        <Filter label="Person" value={filters.person} onChange={(v) => setFilter("person", v)}
          options={[{ value: "all", label: "Everyone" }, ...choices.people]} />
        <Filter label="App" value={filters.app} onChange={(v) => setFilter("app", v)}
          options={[{ value: "all", label: "All apps" }, ...choices.apps]} />
      </div>

      {shown.length === 0 ? (
        <div style={styles.empty}>
          {rows.length === 0 ? "No feedback yet. When someone uses the Feedback tab, it shows up here." : "Nothing matches these filters."}
        </div>
      ) : (
        <div style={styles.list}>
          {shown.map((r) => (
            <FeedbackItem key={r.key} row={r} open={openKey === r.key} onToggle={() => setOpenKey(openKey === r.key ? null : r.key)}
              api={api} onChanged={replaceRow} isAdmin={isAdmin} />
          ))}
        </div>
      )}
    </div>
  );
}

function Filter({ label, value, onChange, options }) {
  return (
    <label style={styles.filter}>
      <span style={styles.filterLabel}>{label}</span>
      <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} style={styles.select}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

const TYPE_COLORS = { bug: C.danger, suggestion: C.blue, question: C.purple, planner: C.teal };
const STATUS_COLORS = { open: C.amber, doing: C.blue, done: C.green, wontfix: C.muted };

function Badge({ color, children }) {
  return <span style={{ ...styles.badge, color, background: `${color}14` }}>{children}</span>;
}

export function FeedbackItem({ row, open, onToggle, api, onChanged, isAdmin = true }) {
  return (
    <div style={{ ...styles.item, ...(open ? styles.itemOpen : {}) }}>
      <button type="button" className="feedback-row" style={styles.rowBtn} onClick={onToggle} aria-expanded={open}>
        <span style={styles.rowBadges}>
          <Badge color={TYPE_COLORS[row.type] || C.muted}>{TYPE_LABELS[row.type] || row.type}</Badge>
          <Badge color={STATUS_COLORS[row.status] || C.muted}>{STATUS_LABELS[row.status]}</Badge>
        </span>
        <span style={styles.rowMain}>
          <span style={styles.rowMessage}>{row.message || "—"}</span>
          <span style={styles.rowMeta}>
            {row.pageLabel} {"·"} {row.personName} {"·"} {formatDate(row.createdAt)} {formatTime(row.createdAt)}
            {row.source === "suite" && row.raw?.picture?.state === "included" ? " · picture" : ""}
          </span>
        </span>
      </button>
      {open && <FeedbackDetail row={row} api={api} onChanged={onChanged} isAdmin={isAdmin} />}
    </div>
  );
}

// Status and note controls are an admin's (the rules and callables refuse anyone else's writes anyway).
export function FeedbackDetail({ row, api, onChanged, isAdmin = true }) {
  const [note, setNote] = useState(row.note || "");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [picture, setPicture] = useState(null); // data URL
  // The list carries a short summary of a Suite report; the whole report is
  // read when it is opened (getSuiteFeedback).
  const [full, setFull] = useState(null);
  const [fullError, setFullError] = useState("");
  const needsFull = row.source === "suite" && row.raw?.summary === true;
  useEffect(() => {
    if (!needsFull || typeof api.get !== "function") return undefined;
    let cancelled = false;
    api.get({ id: row.id }).then((r) => { if (!cancelled) setFull(r); }).catch((e) => { if (!cancelled) setFullError(e?.message || "Couldn't load the details."); });
    return () => { cancelled = true; };
  }, [needsFull, api, row.id]);
  // A summary row takes the full report's fields once read; a row already
  // holding a full report (after a save) is used as it is.
  const raw = needsFull
    ? { ...(row.raw || {}), ...(full || {}), status: row.status, picture: row.raw?.picture?.state === "deleted" ? row.raw.picture : (full?.picture || row.raw?.picture) }
    : (row.raw || {});
  // The note as stored: the full report's, not the list's cut-down copy.
  const savedNote = needsFull ? (full ? (full.note || "") : null) : (row.note || "");
  const noteEdited = useRef(false);
  useEffect(() => {
    if (savedNote !== null && !noteEdited.current) setNote(savedNote);
  }, [savedNote]);
  const isSuite = row.source === "suite";

  const save = async (patch) => {
    setBusy(patch.status ? `status:${patch.status}` : "note");
    setError("");
    try {
      if (isSuite) {
        const item = await api.update({ id: row.id, ...patch });
        onChanged(suiteRow(item));
      } else {
        const stored = {};
        if (patch.status) stored.status = plannerStoredStatus(patch.status);
        if (patch.note !== undefined) stored.resolutionNote = patch.note;
        await dataService.feedback.update(row.sessionId, row.id, stored);
        onChanged({ ...row, ...(patch.status ? { status: patch.status } : {}), ...(patch.note !== undefined ? { note: patch.note } : {}),
          raw: { ...raw, ...stored } });
      }
    } catch (e) {
      setError(e?.message ? `Couldn't save: ${e.message}` : "Couldn't save. Try again.");
      setBusy("");
      return false;
    }
    setBusy("");
    return true;
  };

  const showPicture = async () => {
    setBusy("picture");
    setError("");
    try {
      const res = await api.getPicture({ id: row.id });
      setPicture(`data:image/jpeg;base64,${res.jpegBase64}`);
    } catch (e) {
      setError(e?.message || "Couldn't load the picture.");
    }
    setBusy("");
  };

  const deletePicture = async () => {
    if (!window.confirm("Delete this picture? The feedback itself stays.")) return;
    setBusy("delete");
    setError("");
    try {
      await api.deletePicture({ id: row.id });
      setPicture(null);
      onChanged(suiteRow({ ...raw, picture: { state: "deleted", bytes: null } }));
    } catch (e) {
      setError(e?.message || "Couldn't delete the picture.");
    }
    setBusy("");
  };

  const page = raw.page || {};
  const device = raw.device || {};
  // Only once the full report is read: the summary has no ids, so an early
  // link would open the app without the job.
  const link = isSuite && !(needsFull && !full) ? pageLink(page) : null;
  const pictureState = raw.picture?.state;

  return (
    <div className="feedback-detail" style={styles.detail}>
      {isSuite && <p style={styles.message}>{raw.message || row.message || "—"}</p>}

      {isSuite && (
        <dl style={styles.facts}>
          <dt>Page</dt>
          <dd>
            {row.pageLabel || "Home"}
            {link && <> {"·"} <a href={link} target="_blank" rel="noopener noreferrer">Open this page</a></>}
          </dd>
          {describeIds(page.ids) && (<><dt>Open on it</dt><dd style={{ fontFamily: mono, fontSize: 11.5 }}>{describeIds(page.ids)}</dd></>)}
          <dt>From</dt>
          <dd>{raw.person?.name || "—"}{raw.person?.email ? ` (${raw.person.email})` : ""}{raw.person?.role ? `, ${raw.person.role}` : ""}</dd>
          {needsFull && !full && (<><dt>Details</dt><dd style={{ color: fullError ? C.danger : C.muted }}>{fullError || "Loading…"}</dd></>)}
          <dt>Device</dt>
          <dd>
            {describeBrowser(device.userAgent) || "Unknown browser"}
            {device.viewportW ? `, window ${device.viewportW}×${device.viewportH}` : ""}
            {device.touch ? ", touch" : ""}
            {device.online === false ? ", offline" : ""}
          </dd>
          <dt>Version</dt>
          <dd style={{ fontFamily: mono, fontSize: 11.5 }}>{raw.build?.commit || "unknown"}{raw.build?.builtAt ? ` (built ${formatDate(Date.parse(raw.build.builtAt))})` : ""}</dd>
          <dt>Errors</dt>
          <dd>
            {Array.isArray(raw.errors) && raw.errors.length > 0 ? (
              <ul style={styles.errors}>
                {raw.errors.map((e, i) => <li key={i}><span style={{ color: C.muted }}>{e.kind}</span> {e.message}</li>)}
              </ul>
            ) : "None recorded"}
          </dd>
          <dt>Picture</dt>
          <dd>
            {pictureState === "included" ? (
              <>
                {picture ? (
                  <a href={picture} target="_blank" rel="noopener noreferrer"><img src={picture} alt="The page when this was sent" style={styles.picture} /></a>
                ) : (
                  <button type="button" style={styles.smallBtn} onClick={showPicture} disabled={!!busy}>{busy === "picture" ? "Loading…" : "Show picture"}</button>
                )}
                <button type="button" style={styles.linkBtn} onClick={deletePicture} disabled={!!busy}>Delete picture</button>
              </>
            ) : pictureState === "unticked" ? "Not included (unticked)"
              : pictureState === "failed" || pictureState === "rejected" ? "Couldn't be made on their device"
              : pictureState === "deleted" ? "Deleted" : "—"}
          </dd>
        </dl>
      )}
      {!isSuite && (
        <dl style={styles.facts}>
          {Array.isArray(raw.conversationAnnotations) && raw.conversationAnnotations.length > 0 && (<>
            <dt>On messages</dt>
            <dd><ul style={styles.notes}>{raw.conversationAnnotations.map((a, i) => <li key={i}><span style={{ color: C.muted }}>message {a.messageIndex}</span> {a.feedback}</li>)}</ul></dd>
          </>)}
          {Array.isArray(raw.reportAnnotations) && raw.reportAnnotations.length > 0 && (<>
            <dt>On the report</dt>
            <dd><ul style={styles.notes}>{raw.reportAnnotations.map((a, i) => <li key={i}><span style={{ color: C.muted }}>{a.section}</span> {a.feedback}</li>)}</ul></dd>
          </>)}
          {raw.conversationFeedback && (<><dt>Conversation</dt><dd>{raw.conversationFeedback}</dd></>)}
          {raw.reportFeedback && (<><dt>Report</dt><dd>{raw.reportFeedback}</dd></>)}
          <dt>Session</dt><dd>{row.pageLabel}</dd>
          <dt>From</dt><dd>{row.personName}</dd>
          <dt>Ids</dt><dd style={{ fontFamily: mono, fontSize: 11.5 }}>session {row.sessionId} {"·"} feedback {row.id}</dd>
        </dl>
      )}

      {isAdmin && <>
      <div style={styles.statusRow} role="group" aria-label="Status">
        {STATUSES.map((s) => (
          <button key={s} type="button" disabled={!!busy || row.status === s} onClick={() => save({ status: s })}
            style={{ ...styles.statusBtn, ...(row.status === s ? { background: C.text, color: "#FFF", borderColor: C.text } : {}) }}>
            {busy === `status:${s}` ? "…" : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      <label style={styles.noteLabel}>
        Note
        <textarea value={note} onChange={(e) => { noteEdited.current = true; setNote(e.target.value); }} rows={2} maxLength={2000} style={styles.noteBox}
          disabled={savedNote === null || busy === "note"}
          placeholder="What you did about it, or why not" />
      </label>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" style={styles.smallBtn} disabled={!!busy || savedNote === null || note === savedNote} onClick={async () => { if (await save({ note })) noteEdited.current = false; }}>
          {busy === "note" ? "Saving…" : "Save note"}
        </button>
        {error && <span role="alert" style={{ color: C.danger, fontSize: 12 }}>{error}</span>}
      </div>
      </>}
    </div>
  );
}

const styles = {
  summary: { display: "flex", gap: 4, alignItems: "baseline", flexWrap: "wrap", marginBottom: 10, fontSize: 12, fontWeight: 300, color: C.muted },
  note: { fontSize: 12, color: C.muted, marginBottom: 10 },
  filters: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end", marginBottom: 12 },
  filter: { display: "flex", flexDirection: "column", gap: 3, minWidth: 120 },
  filterLabel: { fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.1em", color: C.muted },
  select: { ...sel, padding: "6px 28px 6px 10px", fontSize: 12, background: C.surface, width: "auto" },
  chip: {
    padding: "7px 12px", border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface, color: C.muted,
    fontSize: 12, fontFamily: font, cursor: "pointer", minHeight: 32,
  },
  chipOn: { background: `${C.danger}14`, color: C.danger, borderColor: `${C.danger}55` },
  list: { border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", background: C.surface },
  item: { borderBottom: `1px solid ${C.border}` },
  itemOpen: { background: "#FAFAF8" },
  rowBtn: {
    display: "flex", gap: 12, alignItems: "flex-start", width: "100%", textAlign: "left", padding: "12px 16px",
    background: "transparent", border: "none", cursor: "pointer", fontFamily: font, color: C.text,
  },
  rowBadges: { display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start", width: 92, flexShrink: 0 },
  rowMain: { display: "flex", flexDirection: "column", gap: 3, minWidth: 0, flex: 1 },
  rowMessage: { fontSize: 13, fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rowMeta: { fontSize: 11, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  badge: {
    display: "inline-block", padding: "2px 7px", borderRadius: 4, fontSize: 9.5, fontWeight: 500,
    textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap",
  },
  detail: { padding: "0 16px 16px 120px", fontSize: 12.5, color: C.text },
  message: { fontSize: 13.5, lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere", margin: "0 0 12px" },
  facts: { display: "grid", gridTemplateColumns: "90px minmax(0, 1fr)", gap: "6px 12px", margin: "0 0 14px", color: C.text },
  errors: { margin: 0, paddingLeft: 16, fontFamily: mono, fontSize: 11, overflowWrap: "anywhere" },
  notes: { margin: 0, paddingLeft: 16, overflowWrap: "anywhere" },
  picture: { maxWidth: "100%", maxHeight: 360, border: `1px solid ${C.border}`, borderRadius: 6, display: "block", marginBottom: 6 },
  statusRow: { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 },
  statusBtn: {
    padding: "6px 12px", minHeight: 32, border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface,
    color: C.text, fontSize: 12, fontFamily: font, cursor: "pointer",
  },
  noteLabel: { display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: C.muted, marginBottom: 6 },
  noteBox: {
    width: "100%", boxSizing: "border-box", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: 6,
    background: C.surface, fontFamily: font, fontSize: 13, color: C.text, resize: "vertical",
  },
  smallBtn: {
    padding: "6px 12px", minHeight: 30, border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface,
    color: C.text, fontSize: 12, fontFamily: font, cursor: "pointer",
  },
  linkBtn: { background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", textDecoration: "underline", padding: "0 4px", fontFamily: font },
  empty: {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 32, textAlign: "center",
    color: C.muted, fontSize: 13, fontWeight: 300, fontFamily: font,
  },
};
